/**
 * Which speech engine this deployment uses.
 *
 * The switch exists so the engines can be compared on measured accuracy and
 * one of them ramped up — which means it has to be changeable without a code
 * change, and ideally without a deploy.
 *
 * Order of precedence, first hit wins:
 *
 * 1. `localStorage['burfy.voice.engine']` — a per-browser override. This is
 *    what makes a ramp or a rollback a console line rather than a release.
 *    Anything unrecognised is ignored, so a typo cannot take the editor down.
 * 2. `VITE_VOICE_ENGINE` — the deployment default, set at build time.
 * 3. `auto` — the first engine that can actually run here.
 */

import {
  type SpeechEngine,
  type SpeechEnginePreference,
  parseEnginePreference,
} from '@burfy/input-tools';
import { createCloudSpeechEngine, createWebSpeechEngine } from '@burfy/docs-editor';

export const ENGINE_OVERRIDE_KEY = 'burfy.voice.engine';

function storedPreference(): string | null {
  try {
    return localStorage.getItem(ENGINE_OVERRIDE_KEY);
  } catch {
    // Private mode, or storage disabled entirely. Not a reason to lose voice
    // typing — fall through to the build-time default.
    return null;
  }
}

export function resolveEnginePreference(): SpeechEnginePreference {
  const buildDefault = parseEnginePreference(import.meta.env.VITE_VOICE_ENGINE);
  return parseEnginePreference(storedPreference(), buildDefault);
}

/**
 * The engines compiled into this app, in the order `auto` should prefer them.
 *
 * The browser first: it needs no server and costs nothing. The cloud engine
 * reports itself unavailable until `VITE_VOICE_CLOUD_ENDPOINT` is set, so
 * `auto` cannot pick an engine that has nowhere to send audio.
 */
export function createEngines(): readonly SpeechEngine[] {
  return [
    createWebSpeechEngine(),
    createCloudSpeechEngine({ endpoint: import.meta.env.VITE_VOICE_CLOUD_ENDPOINT }),
  ];
}
